// Scheduled reminders: Pending / Held / Needs attention / All, with cancel and the reminder dialog.

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type MeResponse, type ScheduledMessage } from '../../api';
import { Loading, ErrorBox, Empty } from '../../components/States';
import { useToast } from '../../components/Toast';
import { displayName, fmtDateAtTime } from '../../lib/format';
import { SCHEDULED_WORDS, scheduledTone } from '../../lib/words';
import { ReminderDialog } from './ReminderDialog';

type Tab = 'pending' | 'held' | 'needs_attention' | 'all';
const TABS: { id: Tab; label: string }[] = [
  { id: 'pending', label: 'Pending' },
  { id: 'held', label: 'Held' },
  { id: 'needs_attention', label: 'Needs attention' },
  { id: 'all', label: 'All' },
];

export function ScheduledPage({ me }: { me: MeResponse }) {
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab) || 'pending';
  const [adding, setAdding] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery({ queryKey: ['scheduled', tab], queryFn: () => api.scheduled.list(tab) });
  const cancel = useMutation({
    mutationFn: (id: string) => api.scheduled.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduled'] });
      toast({ text: 'Reminder cancelled.', tone: 'ok' });
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });

  const items = q.data?.items ?? [];

  return (
    <div className="page">
      <div className="page-head">
        <h1>Scheduled</h1>
        <div className="row">
          <Link to="/scheduled/templates" className="btn">
            Templates
          </Link>
          <button className="btn primary" onClick={() => setAdding(true)}>
            Schedule reminder
          </button>
        </div>
      </div>
      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'active' : ''} onClick={() => setParams({ tab: t.id })}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="card" style={{ padding: 0 }}>
        {q.isPending && <Loading />}
        {q.isError && (
          <div style={{ padding: 16 }}>
            <ErrorBox error={q.error} retry={() => q.refetch()} />
          </div>
        )}
        {q.isSuccess && items.length === 0 && <Empty title={emptyTitle(tab)} hint={emptyHint(tab)} />}
        {items.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Goes out</th>
                  <th>Text</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <Row key={s.id} s={s} tz={me.workspace.timezone} onCancel={() => cancel.mutate(s.id)} cancelling={cancel.isPending} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {adding && <ReminderDialog me={me} onClose={() => setAdding(false)} />}
    </div>
  );
}

function Row({ s, tz, onCancel, cancelling }: { s: ScheduledMessage; tz: string; onCancel: () => void; cancelling: boolean }) {
  const tone = scheduledTone(s.status);
  const detail =
    s.status === 'held' ? (s.held_reason === 'opt_out_suspected' ? 'Waiting: the customer may have opted out. Decide in the thread.' : s.held_reason) : s.status === 'failed' || s.status === 'skipped_opted_out' ? s.error : null;
  return (
    <tr>
      <td>
        {s.contact ? <Link to={`/contacts/${s.contact.id}`}>{displayName(s.contact)}</Link> : <span className="muted">Unknown</span>}
        {s.conversation_id && (
          <div className="tiny">
            <Link to={`/inbox/${s.conversation_id}`}>open thread</Link>
          </div>
        )}
      </td>
      <td className="nowrap">{fmtDateAtTime(s.send_at, tz)}</td>
      <td>
        <div style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>{s.preview ?? s.body ?? (s.template ? `Template: ${s.template.name}` : '')}</div>
        {s.template && <div className="tiny muted">from template “{s.template.name}”</div>}
        {(s.segments ?? 0) >= 2 && <div className={`tiny ${(s.segments ?? 0) >= 3 ? 'danger-text' : 'warn-text'}`}>{s.segments} segments{s.status === 'pending' || s.status === 'held' ? ' (longest wording)' : ''}</div>}
      </td>
      <td>
        <span className={`chip ${tone}`}>{SCHEDULED_WORDS[s.status] ?? s.status}</span>
        {detail && <div className="tiny muted">{detail}</div>}
      </td>
      <td className="right">
        {(s.status === 'pending' || s.status === 'held') && (
          <button className="btn small" onClick={() => confirm('Cancel this reminder?') && onCancel()} disabled={cancelling}>
            Cancel
          </button>
        )}
      </td>
    </tr>
  );
}

function emptyTitle(t: Tab): string {
  switch (t) {
    case 'pending': return 'No reminders waiting';
    case 'held': return 'Nothing on hold';
    case 'needs_attention': return 'Nothing needs attention';
    case 'all': return 'No reminders yet';
  }
}
function emptyHint(t: Tab): string {
  switch (t) {
    case 'pending': return 'Schedule one from a conversation or with the button above.';
    case 'held': return 'A reminder is held when the customer may have asked to stop; you decide in the thread.';
    case 'needs_attention': return 'Failed or skipped reminders show up here.';
    case 'all': return 'Reminders you schedule appear here with their status.';
  }
}
